import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  BankCashMovementDetailSchema,
  BankCashTransactionSchema,
  type AccountDto,
  type AssetDto,
  type BankCashAccountDto,
  type ClientDto,
  type EmployeeDto,
  type ProjectDto,
} from '@taqon/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { ActiveClientPicker, ActiveProjectPicker } from '../crm/common';
import { AssetPicker } from '../procurement/purchasing';
import {
  DimensionRef,
  Facts,
  JournalRef,
  RecordFailure,
  RecordLink,
  useDimensionNames,
} from './detail';
import { businessToday, requirementText } from './line-editor';
import { AccountPicker, EmployeePicker, MasterSelect, useActiveMasters } from './lookups';
import { isAmount, toFils } from './money';
import { RefusalState } from './refusal';

/**
 * Bank and cash movements (FRD-FIN-092, FIN-097; MD-08): every register's, or one register's,
 * with a direct deposit or withdrawal recorded against a contra account the user names — the COA
 * validates it at posting like any other account; none is assumed here. A supplier payment's
 * movement is realised by the payment and posts with it, so it links there instead.
 *
 * Receipts are Accounts Receivable, which is not built; statement import and reconciliation are
 * Phase 2 (FRD-FIN-098, 099). Only the reconciliation status is shown.
 */
export function Movements({
  registers,
  registerId,
  onRegisterChange,
}: {
  registers: readonly BankCashAccountDto[];
  registerId: string;
  onRegisterChange: (registerId: string) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'finance.bank_cash_transaction:create');
  const canPost = hasCapability(session.data, 'finance.bank_cash_transaction:post');
  const [recording, setRecording] = useState(false);
  const movements = useQuery({
    queryKey: ['bank-movements', registerId],
    queryFn: () =>
      getPage(
        '/api/v1/finance/bank-cash-transactions?pageSize=50&sort=-transaction_date' +
          (registerId === '' ? '' : `&filter[bank_cash_account_id]=${registerId}`),
        BankCashTransactionSchema,
      ),
  });
  const post = useMutation({
    mutationFn: (movement: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/bank-cash-transactions/${movement.id}/post`,
        {},
        BankCashTransactionSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: movement.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });
  const codeOf = (id: string) => registers.find((r) => r.id === id)?.code ?? 'Not visible';

  return (
    <Section title="Movements">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ p: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <TextField
          select
          size="small"
          label="Register"
          value={registerId}
          sx={{ minWidth: 240 }}
          onChange={(e) => {
            onRegisterChange(e.target.value);
          }}
        >
          <MenuItem value="">All registers</MenuItem>
          {registers.map((register) => (
            <MenuItem key={register.id} value={register.id}>
              {register.code} — {register.name}
            </MenuItem>
          ))}
        </TextField>
        {canCreate && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(event) => {
              // The dialog returns focus here when it closes.
              event.currentTarget.focus();
              setRecording(true);
            }}
          >
            Record deposit or withdrawal
          </Button>
        )}
      </Stack>
      {post.isError && <RefusalState error={post.error} />}
      {movements.isError && <ErrorState message={movements.error.message} />}
      {movements.isPending && <LoadingState label="Loading movements" />}
      {movements.data?.items.length === 0 && <EmptyState title="No movements" />}
      {movements.data !== undefined && movements.data.items.length > 0 && (
        <Table size="small" aria-label="Bank and cash movements">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Register</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Reconciliation</TableCell>
              <TableCell>Status</TableCell>
              {canPost && <TableCell align="right">Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.data.items.map((movement) => (
              <TableRow key={movement.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <RecordLink to={`/bank-cash/movements/${movement.id}`}>
                    {movement.transactionDate}
                  </RecordLink>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {codeOf(movement.bankCashAccountId)}
                </TableCell>
                <TableCell>{movement.kind}</TableCell>
                <TableCell align="right">{movement.amount}</TableCell>
                <TableCell>{movement.reference ?? '—'}</TableCell>
                <TableCell>{movement.reconciliationStatus}</TableCell>
                <TableCell>
                  <StatusChip status={movement.status} />
                </TableCell>
                {canPost && (
                  <TableCell align="right">
                    {/* A settlement movement posts with the document that realises it (MD-08). */}
                    {movement.status === 'draft' && movement.contraAccountId !== null && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={post.isPending}
                        onClick={() => {
                          post.mutate({ id: movement.id, version: movement.version });
                        }}
                      >
                        Post
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {recording && (
        <RecordMovementDialog
          registers={registers.filter((r) => r.status === 'active')}
          initialRegisterId={registerId}
          onClose={() => {
            setRecording(false);
          }}
        />
      )}
    </Section>
  );
}

function RecordMovementDialog({
  registers,
  initialRegisterId,
  onClose,
}: {
  registers: readonly BankCashAccountDto[];
  initialRegisterId: string;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [registerId, setRegisterId] = useState(
    registers.some((r) => r.id === initialRegisterId) ? initialRegisterId : '',
  );
  const [kind, setKind] = useState<'deposit' | 'withdrawal'>('deposit');
  const [date, setDate] = useState(() => businessToday(session.data?.businessTimezone));
  const [amount, setAmount] = useState('');
  const [contra, setContra] = useState<AccountDto | null>(null);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [client, setClient] = useState<ClientDto | null>(null);
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [asset, setAsset] = useState<AssetDto | null>(null);
  const [locationId, setLocationId] = useState('');
  const canSeeCostCentres = hasCapability(session.data, 'organization.cost_center:view');
  const canSeeLocations = hasCapability(session.data, 'organization.location:view');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeClients = hasCapability(session.data, 'crm.client:view');
  const canSeeProjects = hasCapability(session.data, 'projects.project:view');
  const canSeeAssets = hasCapability(session.data, 'assets.asset:view');
  const costCentres = useActiveMasters('/api/v1/organization/cost-centers', canSeeCostCentres);
  const locations = useActiveMasters('/api/v1/organization/locations', canSeeLocations);
  // The contra account's COA flags decide which dimensions the posting needs (FRD-FIN-020).
  const needsCostCentre = contra?.requires.costCenter === true;
  const needsEmployee = contra?.requires.employee === true;
  const needsClient = contra?.requires.client === true;
  const needsProject = contra?.requires.project === true;
  const needsAsset = contra?.requires.asset === true;
  const record = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/finance/bank-cash-transactions',
        {
          bankCashAccountId: registerId,
          transactionDate: date,
          kind,
          amount: amount.trim(),
          contraAccountId: contra?.id ?? '',
          ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
          ...(description.trim() !== '' ? { description: description.trim() } : {}),
          ...(costCenterId !== '' ? { costCenterId } : {}),
          ...(employee !== null ? { employeeId: employee.id } : {}),
          ...(client !== null ? { clientId: client.id } : {}),
          ...(project !== null ? { projectId: project.id } : {}),
          ...(asset !== null ? { assetId: asset.id } : {}),
          ...(locationId !== '' ? { locationId } : {}),
        },
        BankCashTransactionSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: async (created) => {
      await refreshAfterPosting(queryClient);
      onClose();
      await navigate(`/bank-cash/movements/${created.id}`);
    },
  });
  const amountOk = isAmount(amount) && toFils(amount) > 0n;
  return (
    <FormDialog
      open
      title="Record a deposit or withdrawal"
      submitLabel="Save as draft"
      canSubmit={
        registerId !== '' &&
        date !== '' &&
        amountOk &&
        contra !== null &&
        (!needsCostCentre || costCenterId !== '') &&
        (!needsEmployee || employee !== null) &&
        (!needsClient || client !== null) &&
        (!needsProject || project !== null) &&
        (!needsAsset || asset !== null)
      }
      submitting={record.isPending}
      error={record.isError ? record.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        record.mutate();
      }}
    >
      <TextField
        select
        label="Register"
        required
        value={registerId}
        helperText="An active bank or cash register"
        onChange={(e) => {
          setRegisterId(e.target.value);
        }}
      >
        {registers.map((register) => (
          <MenuItem key={register.id} value={register.id}>
            {register.code} — {register.name}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Type"
          fullWidth
          value={kind}
          onChange={(e) => {
            setKind(e.target.value === 'withdrawal' ? 'withdrawal' : 'deposit');
          }}
        >
          <MenuItem value="deposit">Deposit (money in)</MenuItem>
          <MenuItem value="withdrawal">Withdrawal (money out)</MenuItem>
        </TextField>
        <TextField
          type="date"
          label="Date"
          required
          fullWidth
          value={date}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
      </Stack>
      <TextField
        label="Amount (AED)"
        required
        value={amount}
        error={amount.trim() !== '' && !amountOk}
        helperText={
          amount.trim() !== '' && !amountOk ? 'A positive amount, at most 2 decimals' : undefined
        }
        onChange={(e) => {
          setAmount(e.target.value);
        }}
      />
      <Box>
        <AccountPicker
          value={contra}
          onChange={setContra}
          helperText={
            contra === null
              ? 'The other side of the posting. The Chart of Accounts rules apply when it is posted.'
              : requirementText(contra)
          }
        />
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MasterSelect
          label="Cost centre"
          value={costCenterId}
          required={needsCostCentre}
          disabled={!canSeeCostCentres}
          error={needsCostCentre && costCenterId === ''}
          helperText={canSeeCostCentres ? undefined : 'Needs permission to view cost centres'}
          options={costCentres.data?.items ?? []}
          onChange={setCostCenterId}
        />
        <MasterSelect
          label="Location"
          value={locationId}
          disabled={!canSeeLocations}
          helperText={canSeeLocations ? undefined : 'Needs permission to view locations'}
          options={locations.data?.items ?? []}
          onChange={setLocationId}
        />
      </Stack>
      <EmployeePicker
        value={employee}
        required={needsEmployee}
        disabled={!canSeeEmployees}
        error={needsEmployee && employee === null}
        helperText={canSeeEmployees ? undefined : 'Needs permission to view employees'}
        onChange={setEmployee}
      />
      <ActiveClientPicker
        label="Client"
        value={client}
        required={needsClient}
        disabled={!canSeeClients}
        error={needsClient && client === null}
        helperText={canSeeClients ? undefined : 'Needs permission to view clients'}
        onChange={setClient}
      />
      <ActiveProjectPicker
        label="Project"
        value={project}
        required={needsProject}
        disabled={!canSeeProjects}
        error={needsProject && project === null}
        helperText={canSeeProjects ? undefined : 'Needs permission to view projects'}
        onChange={setProject}
      />
      <AssetPicker
        value={asset}
        required={needsAsset}
        disabled={!canSeeAssets}
        error={needsAsset && asset === null}
        helperText={canSeeAssets ? undefined : 'Needs permission to view assets'}
        onChange={setAsset}
      />
      <TextField
        label="Reference"
        value={reference}
        onChange={(e) => {
          setReference(e.target.value);
        }}
      />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
    </FormDialog>
  );
}

/**
 * One movement (`/bank-cash/movements/:id`): register, type, date, amount, reference and
 * description, reconciliation status, posting status and journal — or, for a supplier payment's
 * movement, the payment that realises it (MD-08).
 */
export function MovementDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canPost = hasCapability(session.data, 'finance.bank_cash_transaction:post');
  const canReconcile = hasCapability(session.data, 'finance.bank_cash_transaction:change');
  const names = useDimensionNames();
  const detail = useQuery({
    queryKey: ['bank-movement', id],
    queryFn: () =>
      getData(`/api/v1/finance/bank-cash-transactions/${id}`, BankCashMovementDetailSchema),
  });
  const post = useMutation({
    mutationFn: (movement: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/bank-cash-transactions/${movement.id}/post`,
        {},
        BankCashTransactionSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: movement.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });
  // FRD-FIN-097: the reconciliation status of a posted movement, set by an authorised user. No
  // statement matching stands behind it (FRD-FIN-098, 099 are Phase 2).
  const reconcile = useMutation({
    mutationFn: (change: { id: string; version: number; status: string }) =>
      patchJson(
        `/api/v1/finance/bank-cash-transactions/${change.id}`,
        { reconciliationStatus: change.status },
        BankCashTransactionSchema,
        session.data?.csrfToken ?? '',
        change.version,
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });
  const back = (
    <Button component={RouterLink} to="/bank-cash" startIcon={<ArrowBackIcon />}>
      Bank &amp; cash
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the movement" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Bank/cash movement" noun="movement" error={detail.error} back={back} />
    );
  }
  const { movement, register, contraAccount, payment } = detail.data;
  const direct = movement.contraAccountId !== null;
  const reconciled = movement.reconciliationStatus === 'reconciled';
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Bank/cash ${movement.kind} of ${movement.amount}`}
        {...(register === null ? {} : { description: `${register.code} — ${register.name}` })}
        actions={
          <>
            {back}
            {canReconcile && movement.status === 'posted' && (
              <Button
                variant="outlined"
                disabled={reconcile.isPending}
                onClick={() => {
                  reconcile.mutate({
                    id: movement.id,
                    version: movement.version,
                    status: reconciled ? 'unreconciled' : 'reconciled',
                  });
                }}
              >
                {reconciled ? 'Mark unreconciled' : 'Mark reconciled'}
              </Button>
            )}
            {canPost && direct && movement.status === 'draft' && (
              <Button
                variant="contained"
                disabled={post.isPending}
                onClick={() => {
                  post.mutate({ id: movement.id, version: movement.version });
                }}
              >
                {post.isPending ? 'Posting…' : 'Post'}
              </Button>
            )}
          </>
        }
      />
      {post.isError && <RefusalState error={post.error} />}
      {reconcile.isError && <RefusalState error={reconcile.error} />}
      <Section title="Movement">
        <Facts
          label="Movement details"
          facts={[
            ['Status', <StatusChip key="s" status={movement.status} />],
            ['Register', register === null ? 'Not visible' : `${register.code} — ${register.name}`],
            ['Type', movement.kind],
            ['Date', movement.transactionDate],
            ['Amount', movement.amount],
            ['Reference', movement.reference ?? '—'],
            ['Description', movement.description ?? '—'],
            [
              'Contra account',
              contraAccount === null
                ? direct
                  ? 'Not visible'
                  : '— (settlement movement)'
                : `${contraAccount.code} — ${contraAccount.name}`,
            ],
            ...(direct
              ? ([
                  ['Cost centre', names.costCentre(movement.costCenterId)],
                  ['Employee', names.employee(movement.employeeId)],
                  ['Client', <DimensionRef key="c" kind="client" id={movement.clientId} />],
                  ['Project', <DimensionRef key="p" kind="project" id={movement.projectId} />],
                  ['Asset', <DimensionRef key="a" kind="asset" id={movement.assetId} />],
                  ['Location', names.location(movement.locationId)],
                ] as [string, ReactNode][])
              : []),
            ['Reconciliation', movement.reconciliationStatus],
            ...(direct
              ? []
              : ([
                  [
                    'Realised by',
                    payment === null ? (
                      'Not visible'
                    ) : (
                      <RecordLink key="p" to={`/payments/${payment.id}`}>
                        Payment {payment.documentNo} ({payment.status})
                      </RecordLink>
                    ),
                  ],
                ] as [string, ReactNode][])),
            [
              'Journal',
              <JournalRef
                key="journal"
                journal={direct ? detail.data.journal : (payment?.journal ?? null)}
              />,
            ],
          ]}
        />
      </Section>
      <DocumentsPanel
        recordType="finance.bank_cash_transaction"
        recordId={movement.id}
        canAttach={
          hasCapability(session.data, 'finance.bank_cash_transaction:create') ||
          hasCapability(session.data, 'finance.bank_cash_transaction:change')
        }
        title="Supporting documents"
      />
      <RecordHistory recordType="finance.bank_cash_transaction" recordId={movement.id} />
    </Stack>
  );
}
